/**
 * **Escort/Extraction** (Core pp. 269–270) — the shared objective-extraction victory read
 * for both types (Escort's 8-round cap vs. Extraction's 10-round cap differ only in
 * `sitrep.roundLimit`, never in this predicate's logic). RAW: the Objective
 * (`sitrep.objectiveId`, a `spawnObject` Combatant at Evasion 10 / E-Def 10 / 0 Armor /
 * 10 HP×SIZE) must reach an `extraction`-kind zone, adjacent to a PC character,
 * uncontested by any enemy character, to win; destroyed or removed from the field ⇒
 * **draw**; neither happens by the round cap ⇒ enemy win.
 *
 * **Branch order (checked in this priority):**
 *   1. **draw** — the objective is destroyed/gone. Checked FIRST: a destroyed
 *      objective can't also read as a PC win.
 *   2. **PC win** — the objective is in an extraction zone, adjacent to ≥1 PC
 *      character, adjacent to 0 enemy characters.
 *   3. **enemy win** — neither of the above, and the round cap has been reached.
 *   4. **active** — none of the above yet.
 *
 * **PC-win and draw are CONTINUOUS checks** — either can fire on any round, not
 * just at the round cap (RAW's extraction/destruction ends the fight the moment it
 * happens). Only the "ran out the clock" enemy-win verdict waits for
 * {@link roundLimitReached}.
 *
 * **`sitrep.objectiveId` unset** (the GM hasn't spawned/linked the Objective via
 * `sitrep spawn-objective` yet) skips the draw/PC-win checks entirely and falls through
 * to the round-cap arm — **an unset id is deliberately NOT a draw**. "Gone" means "was
 * spawned, then removed," not "never existed": reading a fight that hasn't finished
 * setting up as an instant draw would be a strange verdict.
 *
 * **Adjacency** reuses `combatantRange` (`../../effects/spatialAnswerer` —
 * footprint-to-footprint minimum `@lancer-vtt/space` distance, the SAME primitive
 * `mine.ts`'s trigger sweep / disarm-adjacency gate and Overwatch's threat-range check
 * use) `=== 1`, matching the codebase's one existing "adjacent" idiom (`mine.ts`'s
 * disarm gate: `dist !== 1` ⇒ not adjacent). Only {@link isCharacter} combatants
 * (mech/npc/pilot/drone) count toward adjacency, mirroring {@link "../zones" controlOf}'s
 * "only characters contest" RAW read — the objective itself is excluded from its own
 * adjacency scan.
 */
import { isAble, isCharacter, sideOf, type EncounterState, type SitrepConfig } from "@lancer-vtt/domain";
import { combatantRange } from "../../effects/spatialAnswerer";
import type { SitrepStatus } from "../status";
import { roundLimitReached, roundsRemainingFor } from "../roundBoundary";
import { unitsInZone } from "../zones";

export function extractionStatus(state: EncounterState, sitrep: SitrepConfig): SitrepStatus {
  const roundsRemaining = roundsRemainingFor(state, sitrep.roundLimit);

  if (sitrep.objectiveId !== undefined) {
    const objective = state.combatants[sitrep.objectiveId];

    // Draw FIRST — destroyed or removed from the field. Checked ahead of the PC-win
    // arm so a destroyed objective can never also read as extracted.
    if (!objective || !isAble(objective)) {
      return { phase: "decided", winner: "draw" };
    }

    const inExtractionZone = sitrep.zones.some(
      (z) => z.kind === "extraction" && unitsInZone(state, z).some((c) => c.id === objective.id),
    );
    if (inExtractionZone) {
      let adjacentPc = false;
      let adjacentEnemy = false;
      for (const c of Object.values(state.combatants)) {
        if (c.id === objective.id || !isCharacter(c)) continue;
        if (combatantRange(state, objective.id, c.id) !== 1) continue;
        if (sideOf(c) === "pc") adjacentPc = true;
        else adjacentEnemy = true;
      }
      if (adjacentPc && !adjacentEnemy) {
        return { phase: "decided", winner: "pc" };
      }
    }
  }

  if (roundLimitReached(state, sitrep.roundLimit)) {
    return { phase: "decided", winner: "npc" };
  }

  return { phase: "active", roundsRemaining };
}
